import { useEffect, useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import { Navigate } from 'react-router-dom'

import {
  getFnbEodReport,
  getFnbLocations,
  getOrgEventsForReport,
  getOrgVenues,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'
import { EventSelector } from '../EventSelector'
import { EmptyContainer } from '../EmptyContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { ReportActions } from '../ReportActions'
import FnbEodReportCountModal from './FnbEodReportCountModal'
import FnbEodReportHeader from './FnbEodReportHeader'
import FnbEodReportInventoryModal from './FnbEodReportInventoryModal'
import FnbEodReportInventoryTable from './FnbEodReportInventoryTable'
import FnbEodReportPayments from './FnbEodReportPayments'
import FnbEodReportStandMetrics from './FnbEodReportStandMetrics'
import FnbEodReportSummary from './FnbEodReportSummary'
import FnbEodReportVariance from './FnbEodReportVariance'
import { eventDefaultReportDate, todayReportDate } from './fnbEodReportUtils'
import { exportFnbEodReportToExcel } from './fnbEodReportExcelExport'

import './fnbEodReportWrapper.scss'

function getLocationAttrs(location) {
  return location.attributes ?? location
}

export default function FnbEodReportWrapper({ organization }) {
  const [events, setEvents] = useState([])
  const [venues, setVenues] = useState([])
  const [locations, setLocations] = useState([])
  const [eventUUID, setEventUUID] = useState('')
  const [venueUuid, setVenueUuid] = useState('')
  const [locationId, setLocationId] = useState('')
  const [reportDate, setReportDate] = useState(todayReportDate)
  const [reportData, setReportData] = useState(null)
  const [isLoadingEvents, setIsLoadingEvents] = useState(true)
  const [isLoadingVenues, setIsLoadingVenues] = useState(true)
  const [isLoadingLocations, setIsLoadingLocations] = useState(true)
  const [isLoadingReport, setIsLoadingReport] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingReport)
  const [loadError, setLoadError] = useState(null)
  const [showInventoryModal, setShowInventoryModal] = useState(false)
  const [showCountModal, setShowCountModal] = useState(false)

  const organizationUUID = organization?.uuid
  const fnbEnabled = Boolean(organization?.setting?.foodAndBeverage)

  const selectedEvent = useMemo(
    () => events.find((event) => event.uuid === eventUUID) || null,
    [events, eventUUID]
  )

  const requiresVenueSelection = venues.length > 1
  const defaultVenueUuid = venues.length === 1 ? (venues[0]?.uuid ?? '') : ''
  const effectiveVenueUuid = venueUuid || defaultVenueUuid

  const visibleLocations = useMemo(() => {
    if (!effectiveVenueUuid) return locations

    return locations.filter((location) => {
      const attrs = getLocationAttrs(location)
      if (!attrs.venue_uuid) return true
      return attrs.venue_uuid === effectiveVenueUuid
    })
  }, [locations, effectiveVenueUuid])

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingEvents(true)

    getOrgEventsForReport(organizationUUID)
      .then((res) => {
        if (cancelled) return
        setEvents(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load events', err)
        setLoadError('Could not load events for this organization.')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingEvents(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingVenues(true)

    getOrgVenues(organizationUUID)
      .then((res) => {
        if (cancelled) return
        setVenues(Array.isArray(res?.data) ? res.data : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load venues', err)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingVenues(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (venues.length !== 1 || venueUuid) return
    const uuid = venues[0]?.uuid
    if (uuid) setVenueUuid(uuid)
  }, [venues, venueUuid])

  useEffect(() => {
    if (!organizationUUID) return

    let cancelled = false
    setIsLoadingLocations(true)

    getFnbLocations(organizationUUID)
      .then((res) => {
        if (cancelled) return
        setLocations(Array.isArray(res?.data?.data) ? res.data.data : [])
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load F&B locations', err)
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLocations(false)
      })

    return () => {
      cancelled = true
    }
  }, [organizationUUID])

  useEffect(() => {
    if (!selectedEvent) return
    setReportDate(eventDefaultReportDate(selectedEvent))
  }, [selectedEvent])

  useEffect(() => {
    if (
      locationId &&
      !visibleLocations.some((location) => String(location.id) === locationId)
    ) {
      setLocationId('')
    }
  }, [visibleLocations, locationId])

  const canLoadReport = Boolean(
    organizationUUID &&
    reportDate &&
    (eventUUID || !requiresVenueSelection || effectiveVenueUuid)
  )

  const reportVenueUuid =
    eventUUID || locationId ? undefined : effectiveVenueUuid || undefined

  useEffect(() => {
    if (!canLoadReport) {
      setReportData(null)
      return
    }

    let cancelled = false
    setIsLoadingReport(true)
    setLoadError(null)

    getFnbEodReport(organizationUUID, {
      eventUUID: eventUUID || undefined,
      venueUuid: reportVenueUuid,
      locationId: locationId || undefined,
      reportDate: reportDate || undefined,
    })
      .then((res) => {
        if (cancelled) return
        setReportData(res?.data ?? null)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Failed to load F&B EOD report', err)
        setLoadError('Could not load the end of day report for this selection.')
        setReportData(null)
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingReport(false)
          completeRefresh()
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    organizationUUID,
    eventUUID,
    reportVenueUuid,
    locationId,
    reportDate,
    refreshToken,
    canLoadReport,
  ])

  if (!fnbEnabled) {
    return <Navigate to="/settings/organization-information" replace />
  }

  const isLoading = isLoadingEvents || isLoadingVenues || isLoadingReport
  const standSelectDisabled =
    isLoadingLocations ||
    (requiresVenueSelection && !effectiveVenueUuid && !eventUUID)
  const hasData =
    reportData &&
    reportData.error !== 'event_not_found' &&
    reportData.error !== 'fnb_not_enabled' &&
    reportData.error !== 'report_date_or_event_required'

  const handleExcelExport = async () => {
    if (!reportData?.meta?.hasData) return
    try {
      await exportFnbEodReportToExcel(reportData, organization?.name || '')
    } catch (err) {
      console.error('Failed to export F&B EOD report', err)
      window.alert(err?.message || 'Could not export this report to Excel.')
    }
  }

  return (
    <section
      className="spacer-sm fnb-eod-report-wrapper"
      id="fnb-eod-report-wrapper"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>F&amp;B End of Day Report</h1>
        </div>
        <p className="section-header-desc">
          Close-of-day inventory and sales reconciliation by event, business
          date, and concession stand for{' '}
          {organization?.name || 'your organization'}.
        </p>
      </header>

      <Card body className="card--md mb-4">
        <ReportActions
          isLoading={isLoadingReport}
          onRefresh={refresh}
          isRefreshing={isLoadingReport}
          selected={eventUUID || reportDate}
          downloadReport={handleExcelExport}
          data={reportData?.meta?.hasData ? reportData : null}
        >
          <Stack
            direction="horizontal"
            className="flex-wrap gap-3 align-items-end flex-grow-1"
          >
            <div className="flex-grow-1" style={{ minWidth: '240px' }}>
              <EventSelector
                events={events}
                selected={eventUUID}
                setSelected={setEventUUID}
                isLoading={isLoadingEvents}
                label="Event (optional)"
                emptyOptionText="All events"
              />
            </div>
            <Form.Group style={{ minWidth: '180px' }}>
              <Form.Label htmlFor="fnb-eod-report-date">Report Date</Form.Label>
              <Form.Control
                id="fnb-eod-report-date"
                type="date"
                value={reportDate}
                onChange={(event) => setReportDate(event.target.value)}
                disabled={isLoadingReport}
              />
            </Form.Group>
            {requiresVenueSelection ? (
              <Form.Group style={{ minWidth: '220px' }}>
                <Form.Label htmlFor="fnb-eod-venue">Venue</Form.Label>
                <Form.Select
                  id="fnb-eod-venue"
                  value={venueUuid}
                  onChange={(event) => {
                    setVenueUuid(event.target.value)
                    setLocationId('')
                  }}
                  disabled={isLoadingVenues || Boolean(eventUUID)}
                >
                  <option value="">
                    {eventUUID ? 'Set by event' : 'Select venue…'}
                  </option>
                  {venues.map((venue) => (
                    <option key={venue.uuid} value={venue.uuid}>
                      {venue.name ?? venue.uuid}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            ) : null}
            <Form.Group style={{ minWidth: '220px' }}>
              <Form.Label htmlFor="fnb-eod-location">Stand</Form.Label>
              <Form.Select
                id="fnb-eod-location"
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
                disabled={standSelectDisabled}
              >
                <option value="">
                  {requiresVenueSelection && !effectiveVenueUuid && !eventUUID
                    ? 'Select venue first…'
                    : 'All stands'}
                </option>
                {visibleLocations.map((location) => {
                  const attrs = getLocationAttrs(location)
                  return (
                    <option key={location.id} value={String(location.id)}>
                      {attrs.name}
                    </option>
                  )
                })}
              </Form.Select>
            </Form.Group>
            {locationId ? (
              <Stack direction="horizontal" gap={2}>
                <div>
                  <Form.Label className="d-block">&nbsp;</Form.Label>
                  <Button
                    variant="outline-primary"
                    onClick={() => setShowInventoryModal(true)}
                    disabled={!reportDate || isLoadingReport}
                  >
                    Manage inventory
                  </Button>
                </div>
                <div>
                  <Form.Label className="d-block">&nbsp;</Form.Label>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShowCountModal(true)}
                    disabled={!reportDate || isLoadingReport}
                  >
                    Enter closing counts
                  </Button>
                </div>
              </Stack>
            ) : null}
          </Stack>
        </ReportActions>
      </Card>

      {!locationId && canLoadReport ? (
        <Alert variant="info" className="mb-4">
          Select a stand to enter beginning inventory, received stock, and waste
          counts. All-stands view shows sales only.
        </Alert>
      ) : null}

      {loadError ? (
        <Card body className="card--md mb-4">
          <p className="text-danger mb-0">{loadError}</p>
        </Card>
      ) : null}

      {!canLoadReport && reportDate && requiresVenueSelection && !eventUUID ? (
        <EmptyContainer>
          <p className="mb-0">
            Select a venue to view the end of day report for this date.
          </p>
        </EmptyContainer>
      ) : !canLoadReport ? (
        <EmptyContainer>
          <p className="mb-0">
            Select a report date to view the end of day report.
          </p>
        </EmptyContainer>
      ) : isLoading ? (
        <PageLoadingContainer style="without-sidebar" />
      ) : reportData?.error === 'event_not_found' ? (
        <EmptyContainer>
          <p className="mb-0">Event not found for this organization.</p>
        </EmptyContainer>
      ) : hasData && !reportData.meta?.hasData ? (
        <EmptyContainer>
          <p className="mb-0">
            No F&amp;B sales recorded
            {selectedEvent ? (
              <>
                {' '}
                for <strong>{selectedEvent.name}</strong>
              </>
            ) : (
              ' for the selected date'
            )}
            {locationId ? ' at the selected stand' : ''} on{' '}
            <strong>{reportDate}</strong>.
          </p>
        </EmptyContainer>
      ) : hasData ? (
        <Stack gap={4}>
          <FnbEodReportHeader header={reportData.header} />
          <FnbEodReportInventoryTable
            items={reportData.items}
            inventoryTrackingAvailable={reportData.inventoryTrackingAvailable}
            inventorySetupRequired={reportData.inventorySetupRequired}
          />
          <FnbEodReportSummary summary={reportData.summary} />
          <FnbEodReportVariance
            variance={reportData.variance}
            varianceAvailable={reportData.varianceAvailable}
            varianceCountsRecorded={reportData.varianceCountsRecorded}
            varianceAllMatched={reportData.varianceAllMatched}
            inventorySetupRequired={reportData.inventorySetupRequired}
            onEnterCounts={
              locationId ? () => setShowCountModal(true) : undefined
            }
          />
          <FnbEodReportPayments paymentSummary={reportData.paymentSummary} />
          <FnbEodReportStandMetrics standMetrics={reportData.standMetrics} />
        </Stack>
      ) : null}

      <FnbEodReportInventoryModal
        show={showInventoryModal}
        onHide={() => setShowInventoryModal(false)}
        organizationUUID={organizationUUID}
        eventUUID={eventUUID || undefined}
        locationId={locationId}
        reportDate={reportDate}
        onSaved={refresh}
      />

      <FnbEodReportCountModal
        show={showCountModal}
        onHide={() => setShowCountModal(false)}
        organizationUUID={organizationUUID}
        eventUUID={eventUUID || undefined}
        locationId={locationId}
        reportDate={reportDate}
        inventorySetupRequired={reportData?.inventorySetupRequired}
        onSaved={refresh}
      />
    </section>
  )
}
